import { faCircleMinus, faCirclePlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Belonging,
    BookingBelongingPhoto,
    BookingBelongingsPage,
    MAX_BOOKING_BELONGING_PHOTOS,
    SaveBookingBelongingInput,
    SavedBookingBelonging
} from '@spike/belonging-model';
import {
    BelongingsStatus,
    fetchBelongingsThunk,
    fetchBookingBelongingsThunk,
    saveBookingBelongingThunk
} from '@spike/belongings-action';
import { fetchThunk } from '@spike/appointments-list-action';
import { AppointmentListItem } from '@spike/appointments-list-model';
import { CLIENT_SIDE_APPOINTMENTS_PAGE_SIZE } from 'components/Bookings2/List/groomingSort';
import { fetchThunk as fetchRoomsThunk, RoomsStatus } from '@spike/rooms-action';
import { Room } from '@spike/room-model';
import { PageResult } from '@spike/model';
import { BookingSelector } from './BookingSelector/BookingSelector';
import { RoomUnitSelector } from './RoomUnitSelector/RoomUnitSelector';
import Pet from '@spike/pet-model';
import useNonInitialEffect from '@versiondos/hooks';
import Notes from 'components/OrderDetail/Notes';
import AddButton from 'components/UI/V3/TagsSelector/AddButton';
import { PetTagTabs } from 'components/UI/V3/PetTag/Tabs/PetTagTabs';
import Button from 'components/UI/V3/Button/Button';
import PhotosSelector from 'components/UI/V3/PhotosSelector';
import { PhotoWrapper } from 'components/UI/V3/PhotosSelector/PhotosSelector';
import { useApiClientWrapper } from 'hooks';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import AddBelongingModal from './AddBelongingModal/AddBelongingModal';
import BelongingPicker from './BelongingPicker/BelongingPicker';
import styles from './OrderDetailBelongingsTab.module.scss';

const toPhotoWrappers = (photos?: Array<BookingBelongingPhoto> | null): Array<PhotoWrapper> =>
    (photos ?? []).map(photo => ({
        id: photo.id,
        url: photo.url,
        fileToUpload: photo.file instanceof File ? photo.file : null,
        destroy: photo.destroy
    }));

const toSavePhotos = (photos: Array<PhotoWrapper>): NonNullable<SaveBookingBelongingInput['photos']> =>
    photos.map(photo => ({
        id: photo.id,
        url: photo.url,
        file: photo.fileToUpload instanceof File ? photo.fileToUpload : undefined,
        destroy: photo.destroy
    }));

export interface OrderBelonging {
    belonging: Belonging;
    quantity: number;
    itemId?: number;
    toDestroy?: boolean;
}

interface BelongingsTabContentProps {
    pets?: Array<Pet>;
    activePet?: Pet;
    onSelectedPetChange?: (pet: Pet) => void;
    bookingId?: number;
    readOnly?: boolean;
    onSaved?: () => void;
    hideSave?: boolean;
    saveRef?: React.MutableRefObject<(() => void) | undefined>;
    onSavingChange?: (saving: boolean) => void;
    onCanSaveChange?: (canSave: boolean) => void;
}

export const BelongingsTabContent: React.FC<BelongingsTabContentProps> = ({
    pets,
    activePet,
    onSelectedPetChange,
    bookingId,
    readOnly,
    onSaved,
    hideSave,
    saveRef,
    onSavingChange,
    onCanSaveChange
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const catalogBelongings = useSelector<RootState, Array<Belonging>>(state => state.belongings.belongings);
    const catalogLoading = useSelector<RootState, boolean>(state => state.belongings.loading);
    const belongingsStatus = useSelector<RootState, BelongingsStatus>(state => state.belongings.status);
    const bookingBelongingsPage = useSelector<RootState, BookingBelongingsPage | undefined>(
        state => state.belongings.bookingBelongingsPage
    );
    const savedBookingBelonging = useSelector<RootState, SavedBookingBelonging | undefined>(
        state => state.belongings.savedBookingBelonging
    );
    const bookingList = useSelector<RootState, Array<AppointmentListItem>>(
        state => state.appointmentsList.all.result.page
    );
    const roomsPage = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const marketplaceId = useSelector<RootState, number | undefined>(state => state.marketplace.marketplace?.id);

    const [selectedBookingId, setSelectedBookingId] = useState<number | undefined>(undefined);
    const [selectedPetId, setSelectedPetId] = useState<number | undefined>(undefined);
    const [selectedRoomUnitId, setSelectedRoomUnitId] = useState<number | undefined>(undefined);
    const [notes, setNotes] = useState('');
    const [showPicker, setShowPicker] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [orderBelongings, setOrderBelongings] = useState<OrderBelonging[]>([]);
    const [photos, setPhotos] = useState<Array<PhotoWrapper>>([]);
    const [isSaving, setIsSaving] = useState(false);

    const activeBookingId = bookingId ?? selectedBookingId;
    const activePetId = activePet?.id ?? selectedPetId;
    const canSave =
        activeBookingId !== undefined && activePetId !== undefined && orderBelongings.some(ob => !ob.toDestroy);

    // Preload existing belongings and notes when fetch completes
    useNonInitialEffect(() => {
        if (belongingsStatus === BelongingsStatus.BookingBelongingsFetchSuccess && bookingBelongingsPage) {
            const entry = bookingBelongingsPage.data.find(
                b => b.bookingId === activeBookingId && b.petId === activePetId
            );
            if (entry) {
                setNotes(entry.notes ?? '');
                setSelectedRoomUnitId(entry.room?.unitId ?? undefined);
                setPhotos(toPhotoWrappers(entry.photos));
                const mapped: OrderBelonging[] = entry.belongings
                    .map(item => {
                        const itemId = item.id;
                        const belongingId = item.belongingId;
                        const catalogItem = belongingId
                            ? catalogBelongings.find(c => c.id === belongingId)
                            : catalogBelongings.find(c => c.name === item.itemName);
                        // Keep the existing item (and its id) even if the catalog hasn't loaded yet or the
                        // belonging is inactive — otherwise we lose the item id and re-saving re-creates it,
                        // hitting the per-set uniqueness on belonging_id (422 "has already been taken").
                        const belonging =
                            catalogItem ??
                            (belongingId !== undefined
                                ? ({ id: belongingId, name: item.itemName } as unknown as Belonging)
                                : undefined);
                        if (!belonging) return null;
                        const ob: OrderBelonging = { belonging, quantity: item.quantity };
                        if (itemId !== undefined) ob.itemId = itemId;
                        return ob;
                    })
                    .filter((ob): ob is OrderBelonging => ob !== null);
                setOrderBelongings(mapped);
            }
        }

        // After a successful save, sync itemIds and photos from the server response
        if (belongingsStatus === BelongingsStatus.BookingBelongingsSaveSuccess && savedBookingBelonging) {
            setOrderBelongings(prev =>
                prev
                    .filter(ob => !ob.toDestroy)
                    .map(ob => {
                        const saved = savedBookingBelonging.belongings.find(s => s.belongingId === ob.belonging.id);
                        return saved ? { ...ob, itemId: saved.id } : ob;
                    })
            );
            setPhotos(toPhotoWrappers(savedBookingBelonging.photos));
            onSaved?.();
        }

        if (belongingsStatus === BelongingsStatus.BookingBelongingsSaveDestroyed) {
            setPhotos([]);
            onSaved?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [belongingsStatus]);

    // Fetch today's booking list on mount (for the standalone booking selector)
    useEffect(() => {
        const today = moment().format('YYYY-MM-DD');
        dispatch(
            fetchThunk(
                apiClientWrapper,
                'all',
                '',
                marketplaceId ? [marketplaceId] : [],
                null,
                today,
                today,
                null,
                [1, 2, 3, 4, 5],
                [],
                [],
                [],
                null,
                null,
                null,
                '',
                1,
                CLIENT_SIDE_APPOINTMENTS_PAGE_SIZE
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch rooms on mount
    useEffect(() => {
        dispatch(fetchRoomsThunk(apiClientWrapper, 1, 1000, marketplaceId ? [marketplaceId] : undefined));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch belongings catalog on mount (for the picker)
    useEffect(() => {
        dispatch(fetchBelongingsThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset selector state when pet changes in standalone mode
    useEffect(() => {
        if (bookingId === undefined) {
            setSelectedBookingId(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePet?.id]);

    // Fetch booking belongings when active booking or pet changes
    useEffect(() => {
        setNotes('');
        setOrderBelongings([]);
        setPhotos([]);

        if (activeBookingId === undefined || activePetId === undefined) return;

        dispatch(fetchBookingBelongingsThunk(apiClientWrapper, { bookingId: activeBookingId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeBookingId, activePetId]);

    // Report save state to a host (e.g. the dashboard drawer footer)
    useEffect(() => {
        onSavingChange?.(isSaving);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSaving]);

    useEffect(() => {
        onCanSaveChange?.(canSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSave]);

    const buildPayload = useCallback(
        (
            currentBelongings: OrderBelonging[],
            currentNotes: string,
            currentPhotos: Array<PhotoWrapper>
        ): SaveBookingBelongingInput => ({
            bookingId: activeBookingId!,
            petId: activePetId!,
            roomUnitId: selectedRoomUnitId,
            note: currentNotes || undefined,
            items: currentBelongings
                .filter(ob => !(ob.toDestroy && ob.itemId === undefined)) // skip unsaved items marked for delete
                .map(ob => {
                    if (ob.toDestroy && ob.itemId !== undefined) {
                        // delete: only id + destroy
                        return { id: ob.itemId, destroy: true as const };
                    }
                    if (ob.itemId !== undefined) {
                        // update: id + belongingId + quantity
                        return { id: ob.itemId, belongingId: ob.belonging.id, quantity: ob.quantity };
                    }
                    // new: only belongingId + quantity, no id
                    return { belongingId: ob.belonging.id, quantity: ob.quantity };
                }),
            photos: toSavePhotos(currentPhotos)
        }),
        [activeBookingId, activePetId, selectedRoomUnitId]
    );

    const handleBelongingSelected = (belonging: Belonging) => {
        setOrderBelongings(prev => [...prev, { belonging, quantity: 1 }]);
        setShowPicker(false);
    };

    const handleBelongingSaved = (belonging: Belonging) => {
        setOrderBelongings(prev => [...prev, { belonging, quantity: 1 }]);
        setShowModal(false);
    };

    const handleQuantityChange = (id: number, delta: number) => {
        setOrderBelongings(prev =>
            prev.map(ob => (ob.belonging.id === id ? { ...ob, quantity: Math.max(1, ob.quantity + delta) } : ob))
        );
    };

    const handleRemoveBelonging = (id: number) => {
        setOrderBelongings(prev =>
            prev.flatMap(ob => {
                if (ob.belonging.id !== id) return [ob];
                if (ob.itemId !== undefined) return [{ ...ob, toDestroy: true }];
                return [];
            })
        );
    };

    const handleSave = async () => {
        if (activeBookingId === undefined || isSaving) return;
        setIsSaving(true);
        try {
            const payload = buildPayload(orderBelongings, notes, photos);
            await dispatch(saveBookingBelongingThunk(apiClientWrapper, payload));
        } finally {
            setIsSaving(false);
        }
    };

    if (saveRef) {
        saveRef.current = handleSave;
    }

    const visibleBelongings = orderBelongings.filter(ob => !ob.toDestroy);

    return (
        <>
            {bookingId === undefined && (
                <div className={styles.bookingSection}>
                    <BookingSelector
                        bookings={bookingList}
                        selectedBookingId={selectedBookingId}
                        onSelect={(bkId, apptId) => {
                            setSelectedBookingId(bkId);
                            const appt = bookingList.find(a => a.appointmentId === apptId);
                            setSelectedPetId(appt?.pet.id);
                        }}
                    />
                </div>
            )}

            {activeBookingId !== undefined && (
                <>
                    {pets && activePet && onSelectedPetChange && (
                        <PetTagTabs
                            pets={pets}
                            value={activePet}
                            onChange={onSelectedPetChange}
                            className={styles.tabs}
                        />
                    )}

                    <div className={styles.header}>
                        <span className={styles.title}>Add Belongings</span>
                    </div>

                    {visibleBelongings.length > 0 && (
                        <div className={styles.tableHeader}>
                            <span className={styles.tableHeaderItem}>Item</span>
                            <span className={styles.tableHeaderQty}>Qty</span>
                        </div>
                    )}

                    {visibleBelongings.map(ob => (
                        <div
                            key={ob.belonging.id}
                            className={styles.rowWrapper}
                        >
                            <div className={styles.tableRow}>
                                <span className={styles.rowName}>{ob.belonging.name}</span>
                                <div className={styles.qtyControls}>
                                    <button
                                        className={styles.qtyButton}
                                        onClick={() => handleQuantityChange(ob.belonging.id, -1)}
                                        disabled={ob.quantity <= 1 || readOnly}
                                    >
                                        <FontAwesomeIcon icon={faCircleMinus} />
                                    </button>
                                    <span className={styles.qty}>{ob.quantity}</span>
                                    <button
                                        className={styles.qtyButton}
                                        onClick={() => handleQuantityChange(ob.belonging.id, 1)}
                                        disabled={readOnly}
                                    >
                                        <FontAwesomeIcon icon={faCirclePlus} />
                                    </button>
                                </div>
                            </div>
                            {!readOnly && (
                                <button
                                    className={styles.removeButton}
                                    onClick={() => handleRemoveBelonging(ob.belonging.id)}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            )}
                        </div>
                    ))}

                    {!readOnly && (
                        <div className={styles.addButtonWrapper}>
                            {showPicker ? (
                                <BelongingPicker
                                    belongings={catalogBelongings}
                                    excludeIds={visibleBelongings.map(ob => ob.belonging.id)}
                                    loading={catalogLoading}
                                    onSelect={handleBelongingSelected}
                                    onAddNew={() => {
                                        setShowPicker(false);
                                        setShowModal(true);
                                    }}
                                    onClose={() => setShowPicker(false)}
                                />
                            ) : (
                                <AddButton
                                    color="#000000"
                                    onClick={() => setShowPicker(true)}
                                />
                            )}
                        </div>
                    )}

                    {!readOnly ? (
                        <PhotosSelector
                            className={styles.photosSelector}
                            size="small"
                            title="Select Photos"
                            photos={photos}
                            maxPhotos={MAX_BOOKING_BELONGING_PHOTOS}
                            multipleSelection
                            onChange={setPhotos}
                        />
                    ) : (
                        photos.some(photo => !photo.destroy) && (
                            <div className={styles.photosSelector}>
                                <span className={styles.photosTitle}>Select Photos</span>
                                <div className={styles.photosReadonlyGrid}>
                                    {photos
                                        .filter(photo => !photo.destroy)
                                        .map(photo => (
                                            <div
                                                key={photo.id ?? photo.url}
                                                className={styles.photoThumb}
                                            >
                                                <img
                                                    src={photo.url}
                                                    alt=""
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )
                    )}

                    <div className={styles.roomSection}>
                        <RoomUnitSelector
                            rooms={roomsPage.page}
                            selectedUnitId={selectedRoomUnitId}
                            onSelect={setSelectedRoomUnitId}
                        />
                    </div>

                    <Notes
                        notes={notes}
                        title="Notes"
                        placeholder="Add additional notes..."
                        onChange={setNotes}
                        disabled={readOnly}
                        className={styles.notes}
                    />

                    {!readOnly && !hideSave && (
                        <div className={styles.saveButtonWrapper}>
                            <Button
                                label="Save"
                                variant="green"
                                onClick={handleSave}
                                disabled={isSaving}
                                isLoading={isSaving}
                                className={styles.saveButton}
                            />
                        </div>
                    )}

                    <div className={styles.bottomSpacer} />

                    {showModal && (
                        <AddBelongingModal
                            onSaved={handleBelongingSaved}
                            onDiscard={() => setShowModal(false)}
                        />
                    )}
                </>
            )}
        </>
    );
};
